import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Package, Plus, Trash2, DollarSign, Clock, Eye, EyeOff, Edit2 } from 'lucide-react';
import { services as servicesApi, addons as addonsApi } from '../utils/api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const ServicesEditor = () => {
  const [activeSubTab, setActiveSubTab] = useState('services');
  const [services, setServices] = useState([]);
  const [addons, setAddons] = useState([]);
  const [sizingEnabled, setSizingEnabled] = useState(true);
  const [servicesEnabled, setServicesEnabled] = useState(true);
  const [addonsEnabled, setAddonsEnabled] = useState(true);
  const [defaultServiceDuration, setDefaultServiceDuration] = useState(60);
  const [defaultDailyCapacity, setDefaultDailyCapacity] = useState(10);
  const [flatRatePrice, setFlatRatePrice] = useState(0);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [editingAddon, setEditingAddon] = useState(null);

  // Service form data
  const [serviceForm, setServiceForm] = useState({
    name: '',
    description: '',
    serviceType: 'other',
    pricing: [
      { size: 'small', price: '', durationMinutes: '' },
      { size: 'medium', price: '', durationMinutes: '' },
      { size: 'large', price: '', durationMinutes: '' },
      { size: 'extra_large', price: '', durationMinutes: '' },
    ],
  });

  // Add-on form data
  const [addonForm, setAddonForm] = useState({
    name: '',
    description: '',
    price: '',
    displayOrder: 0,
  });

  const loadData = useCallback(async () => {
    try {
      console.log('[ServicesEditor] Loading services and add-ons...');
      const [servicesRes, addonsRes, settingsRes, servicesEnabledRes, addonsEnabledRes, defaultDurationRes, defaultCapacityRes, flatRateRes] = await Promise.all([
        servicesApi.getAll({ includeInactive: true }),
        addonsApi.getAll({ includeInactive: true }),
        axios.get(`${API_URL}/size-categories/settings`),
        axios.get(`${API_URL}/business-info/services_enabled`).catch(() => ({ data: { info_value: 'true' } })),
        axios.get(`${API_URL}/business-info/addons_enabled`).catch(() => ({ data: { info_value: 'true' } })),
        axios.get(`${API_URL}/business-info/default_service_duration`).catch(() => ({ data: { info_value: '60' } })),
        axios.get(`${API_URL}/business-info/default_daily_capacity`).catch(() => ({ data: { info_value: '10' } })),
        axios.get(`${API_URL}/business-info/flat_rate_price`).catch(() => ({ data: { info_value: '0' } })),
      ]);
      console.log('[ServicesEditor] Services loaded:', servicesRes.data);
      console.log('[ServicesEditor] Add-ons loaded:', addonsRes.data);
      console.log('[ServicesEditor] Settings response:', settingsRes.data);
      console.log('[ServicesEditor] Sizing enabled value:', settingsRes.data.sizing_enabled);
      console.log('[ServicesEditor] Setting sizingEnabled to:', settingsRes.data.sizing_enabled === 'true');
      setServices(servicesRes.data);
      setAddons(addonsRes.data);
      setSizingEnabled(settingsRes.data.sizing_enabled === 'true');
      setServicesEnabled(servicesEnabledRes.data.info_value !== 'false');
      setAddonsEnabled(addonsEnabledRes.data.info_value !== 'false');
      setDefaultServiceDuration(parseInt(defaultDurationRes.data.info_value) || 60);
      setDefaultDailyCapacity(parseInt(defaultCapacityRes.data.info_value) || 10);
      setFlatRatePrice(parseFloat(flatRateRes.data.info_value) || 0);
    } catch (error) {
      console.error('Failed to load services/add-ons:', error);
      toast.error('Failed to load data: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reload data when size categories are updated
  useEffect(() => {
    const handleSizeCategoryUpdate = () => {
      console.log('[ServicesEditor] sizeCategoryUpdated event received, reloading services...');
      loadData();
    };

    console.log('[ServicesEditor] Setting up sizeCategoryUpdated event listener');
    window.addEventListener('sizeCategoryUpdated', handleSizeCategoryUpdate);
    
    return () => {
      console.log('[ServicesEditor] Removing sizeCategoryUpdated event listener');
      window.removeEventListener('sizeCategoryUpdated', handleSizeCategoryUpdate);
    };
  }, [loadData]);

  // Save feature settings
  const handleSaveFeatureSettings = async () => {
    setSavingSettings(true);
    try {
      const token = sessionStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      };

      await Promise.all([
        axios.put(`${API_URL}/business-info/services_enabled`, {
          value: servicesEnabled.toString(),
        }, config),
        axios.put(`${API_URL}/business-info/addons_enabled`, {
          value: addonsEnabled.toString(),
        }, config),
        axios.put(`${API_URL}/business-info/default_service_duration`, {
          value: defaultServiceDuration.toString(),
        }, config),
        axios.put(`${API_URL}/business-info/default_daily_capacity`, {
          value: defaultDailyCapacity.toString(),
        }, config),
        axios.put(`${API_URL}/business-info/flat_rate_price`, {
          value: flatRatePrice.toString(),
        }, config),
      ]);

      toast.success('Booking features settings saved successfully');
      
      // Switch to an enabled tab if current tab is disabled
      if (!servicesEnabled && activeSubTab === 'services' && addonsEnabled) {
        setActiveSubTab('addons');
      } else if (!addonsEnabled && activeSubTab === 'addons' && servicesEnabled) {
        setActiveSubTab('services');
      }
      
      // Dispatch event to notify other components
      window.dispatchEvent(new Event('bookingFeaturesUpdated'));
    } catch (error) {
      console.error('Failed to save booking features settings:', error);
      toast.error('Failed to save settings: ' + (error.response?.data?.error || error.message));
    } finally {
      setSavingSettings(false);
    }
  };

  // Service handlers
  const handleOpenServiceModal = (service = null) => {
    // Determine default pricing based on sizing enabled state
    const getDefaultPricing = () => {
      if (!sizingEnabled) {
        return [{ size: 'standard', price: '', durationMinutes: '' }];
      }
      return [
        { size: 'small', price: '', durationMinutes: '' },
        { size: 'medium', price: '', durationMinutes: '' },
        { size: 'large', price: '', durationMinutes: '' },
        { size: 'extra_large', price: '', durationMinutes: '' },
      ];
    };

    if (service) {
      setEditingService(service);
      // If sizing is disabled and service has size-based pricing, use first entry as standard
      let pricing = service.pricing || getDefaultPricing();
      if (!sizingEnabled && service.pricing && service.pricing.length > 0) {
        pricing = [{ size: 'standard', price: service.pricing[0].price, durationMinutes: service.pricing[0].durationMinutes }];
      }
      
      setServiceForm({
        name: service.name,
        description: service.description || '',
        serviceType: service.service_type,
        pricing: pricing,
      });
    } else {
      setEditingService(null);
      setServiceForm({
        name: '',
        description: '',
        serviceType: 'other',
        pricing: getDefaultPricing(),
      });
    }
    setShowServiceModal(true);
  };

  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingService) {
        await servicesApi.update(editingService.id, serviceForm);
        toast.success('Service updated successfully');
      } else {
        await servicesApi.create(serviceForm);
        toast.success('Service created successfully');
      }
      loadData();
      setShowServiceModal(false);
      // Notify other components
      window.dispatchEvent(new Event('serviceUpdated'));
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save service');
    }
  };

  const handleToggleService = async (serviceId) => {
    try {
      const service = services.find(s => s.id === serviceId);
      await servicesApi.update(serviceId, { isActive: !service.is_active });
      toast.success('Service status updated');
      loadData();
      // Notify other components
      window.dispatchEvent(new Event('serviceUpdated'));
    } catch (error) {
      toast.error('Failed to update service status');
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to permanently delete this service? This cannot be undone.')) return;
    try {
      await servicesApi.delete(serviceId);
      toast.success('Service deleted permanently');
      loadData();
      // Notify other components
      window.dispatchEvent(new Event('serviceUpdated'));
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  // Add-on handlers
  const handleOpenAddonModal = (addon = null) => {
    if (addon) {
      setEditingAddon(addon);
      setAddonForm({
        name: addon.name,
        description: addon.description || '',
        price: addon.price,
        displayOrder: addon.display_order || 0,
      });
    } else {
      setEditingAddon(null);
      setAddonForm({
        name: '',
        description: '',
        price: '',
        displayOrder: 0,
      });
    }
    setShowAddonModal(true);
  };

  const handleAddonSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAddon) {
        await addonsApi.update(editingAddon.id, addonForm);
        toast.success('Add-on updated successfully');
      } else {
        await addonsApi.create(addonForm);
        toast.success('Add-on created successfully');
      }
      loadData();
      setShowAddonModal(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save add-on');
    }
  };

  const handleToggleAddon = async (addonId) => {
    try {
      await addonsApi.toggle(addonId);
      toast.success('Add-on status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update add-on status');
    }
  };

  const handleDeleteAddon = async (addonId) => {
    if (!window.confirm('Are you sure you want to permanently delete this add-on? This cannot be undone.')) return;
    try {
      await addonsApi.delete(addonId);
      toast.success('Add-on deleted permanently');
      loadData();
    } catch (error) {
      toast.error('Failed to delete add-on');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-4">
        <Package className="text-primary-600" size={24} />
        <h2 className="text-2xl font-bold">Bookable Services & Add-ons</h2>
      </div>

      <p className="text-gray-600 mb-6">
        Manage the actual services and add-ons that clients can book. These will appear in the booking system for clients, admins, and employees.
      </p>

      {/* Enable/Disable Features */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-gray-900 mb-3">Booking Features</h3>
        <p className="text-sm text-gray-600 mb-4">
          Enable or disable features in the booking system. Disabled features will not appear for clients, employees, or admins.
        </p>
        
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="servicesEnabled"
              checked={servicesEnabled}
              onChange={(e) => setServicesEnabled(e.target.checked)}
              className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="servicesEnabled" className="text-sm font-medium text-gray-900 cursor-pointer">
              Enable Services - Allow selecting services when booking appointments
            </label>
          </div>
          
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="addonsEnabled"
              checked={addonsEnabled}
              onChange={(e) => setAddonsEnabled(e.target.checked)}
              className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="addonsEnabled" className="text-sm font-medium text-gray-900 cursor-pointer">
              Enable Add-ons - Allow adding extra services to appointments
            </label>
          </div>

          {/* Default Service Duration & Capacity - Only show when services are disabled */}
          {!servicesEnabled && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg space-y-4">
              <div>
                <label htmlFor="defaultDuration" className="block text-sm font-medium text-gray-900 mb-2">
                  Default Appointment Duration
                </label>
                <p className="text-xs text-gray-600 mb-2">
                  Since services are disabled, set a default duration for appointments. This determines how long each appointment slot will be.
                </p>
                {(() => {
                  const presets = [30, 45, 60, 90, 120, 150, 180, 240];
                  const isCustom = !presets.includes(defaultServiceDuration);
                  return (
                    <div className="flex items-center gap-3">
                      <select
                        id="defaultDuration"
                        value={isCustom ? 'custom' : defaultServiceDuration}
                        onChange={(e) => {
                          if (e.target.value === 'custom') {
                            setDefaultServiceDuration(defaultServiceDuration);
                          } else {
                            setDefaultServiceDuration(parseInt(e.target.value));
                          }
                        }}
                        className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value={30}>30 minutes</option>
                        <option value={45}>45 minutes</option>
                        <option value={60}>1 hour</option>
                        <option value={90}>1.5 hours</option>
                        <option value={120}>2 hours</option>
                        <option value={150}>2.5 hours</option>
                        <option value={180}>3 hours</option>
                        <option value={240}>4 hours</option>
                        <option value="custom">Custom...</option>
                      </select>
                      {isCustom && (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="5"
                            max="480"
                            value={defaultServiceDuration}
                            onChange={(e) => setDefaultServiceDuration(parseInt(e.target.value) || 5)}
                            className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                          <span className="text-sm text-gray-600">minutes</span>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              
              <div>
                <label htmlFor="flatRatePrice" className="block text-sm font-medium text-gray-900 mb-2">
                  <DollarSign size={16} className="inline mr-1" />
                  Flat Rate Price
                </label>
                <p className="text-xs text-gray-600 mb-2">
                  Base price charged for each appointment when services are disabled. Add-on prices will be added on top of this. Set to 0 for no base charge.
                </p>
                <div className="relative w-full max-w-xs">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    id="flatRatePrice"
                    min="0"
                    step="0.01"
                    value={flatRatePrice}
                    onChange={(e) => setFlatRatePrice(parseFloat(e.target.value) || 0)}
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="defaultCapacity" className="block text-sm font-medium text-gray-900 mb-2">
                  Default Daily Capacity
                </label>
                <p className="text-xs text-gray-600 mb-2">
                  Maximum number of appointments that can be booked per day when services are disabled.
                </p>
                <input
                  type="number"
                  id="defaultCapacity"
                  min="1"
                  value={defaultDailyCapacity}
                  onChange={(e) => setDefaultDailyCapacity(parseInt(e.target.value) || 10)}
                  className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          )}
        </div>

        <button
          onClick={handleSaveFeatureSettings}
          disabled={savingSettings}
          className="btn btn-primary mt-4"
        >
          {savingSettings ? 'Saving...' : 'Save Feature Settings'}
        </button>
      </div>

      {/* Sub Tabs - Only show tabs for enabled features */}
      {(servicesEnabled || addonsEnabled) && (
        <div className="flex space-x-1 mb-6 border-b border-gray-200">
          {servicesEnabled && (
            <button
              onClick={() => setActiveSubTab('services')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeSubTab === 'services'
                  ? 'border-b-2 border-primary-600 text-primary-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Services
            </button>
          )}
          {addonsEnabled && (
            <button
              onClick={() => setActiveSubTab('addons')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeSubTab === 'addons'
                  ? 'border-b-2 border-primary-600 text-primary-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Add-ons
            </button>
          )}
        </div>
      )}

      {/* Message when both features are disabled */}
      {!servicesEnabled && !addonsEnabled && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <Package className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            Services and Add-ons are Disabled
          </h3>
          <p className="text-gray-600">
            Enable at least one feature above to manage your bookable services and add-ons.
          </p>
        </div>
      )}

      {/* Services Sub-Tab */}
      {servicesEnabled && activeSubTab === 'services' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-600">
              Services clients can select when booking appointments
            </p>
            <button
              onClick={() => handleOpenServiceModal()}
              className="btn btn-primary flex items-center space-x-2"
            >
              <Plus size={18} />
              <span>Add Service</span>
            </button>
          </div>

          <div className="space-y-3">
            {services.map((service) => (
              <div
                key={service.id}
                className={`p-4 border-2 rounded-lg ${!service.is_active ? 'opacity-60 bg-gray-50' : 'border-gray-200'}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold mb-2">{service.name}</h3>
                    {service.description && (
                      <p className="text-sm text-gray-600 mb-3">{service.description}</p>
                    )}
                    {sizingEnabled ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                        {service.pricing?.map((p) => (
                          <div key={p.size} className="bg-gray-50 p-2 rounded">
                            <div className="font-semibold capitalize">{p.size.replace('_', ' ')}</div>
                            <div className="text-primary-600">${p.price}</div>
                            <div className="text-gray-500 text-xs">{p.durationMinutes}min</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      service.pricing && service.pricing.length > 0 && (
                        <div className="text-sm">
                          <span className="text-primary-600 font-semibold">${service.pricing[0].price}</span>
                          <span className="text-gray-500 ml-2">({service.pricing[0].durationMinutes}min)</span>
                        </div>
                      )
                    )}
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleToggleService(service.id)}
                      className={`p-2 rounded-lg ${
                        service.is_active
                          ? 'bg-green-100 text-green-600'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                      title={service.is_active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
                    >
                      {service.is_active ? <Eye size={18} /> : <EyeOff size={18} />}
                    </button>
                    <button
                      onClick={() => handleOpenServiceModal(service)}
                      className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDeleteService(service.id)}
                      className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add-ons Sub-Tab */}
      {addonsEnabled && activeSubTab === 'addons' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-600">
              Extra services clients can add to their appointments
            </p>
            <button
              onClick={() => handleOpenAddonModal()}
              className="btn btn-primary flex items-center space-x-2"
            >
              <Plus size={18} />
              <span>Add Add-on</span>
            </button>
          </div>

          <div className="space-y-3">
            {addons.map((addon) => (
              <div
                key={addon.id}
                className={`p-4 border-2 rounded-lg ${!addon.is_active ? 'opacity-60 bg-gray-50' : 'border-gray-200'}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold mb-1">{addon.name}</h3>
                    {addon.description && (
                      <p className="text-sm text-gray-600 mb-2">{addon.description}</p>
                    )}
                    <div className="text-xl font-bold text-primary-600">
                      ${parseFloat(addon.price).toFixed(2)}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleToggleAddon(addon.id)}
                      className={`p-2 rounded-lg ${
                        addon.is_active
                          ? 'bg-green-100 text-green-600'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                      title={addon.is_active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
                    >
                      {addon.is_active ? <Eye size={18} /> : <EyeOff size={18} />}
                    </button>
                    <button
                      onClick={() => handleOpenAddonModal(addon)}
                      className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDeleteAddon(addon.id)}
                      className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Service Modal */}
      {showServiceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-2xl font-bold mb-6">
              {editingService ? 'Edit Service' : 'Add New Service'}
            </h2>

            <form onSubmit={handleServiceSubmit} className="space-y-4">
              <div>
                <label className="label">Service Name *</label>
                <input
                  type="text"
                  required
                  className="input"
                  value={serviceForm.name}
                  onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                />
              </div>

              <div>
                <label className="label">Description</label>
                <textarea
                  className="input"
                  rows="3"
                  value={serviceForm.description}
                  onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                />
              </div>

{sizingEnabled ? (
                <div>
                  <label className="label">Pricing by Size *</label>
                  <div className="space-y-3">
                    {serviceForm.pricing.map((pricing, index) => (
                      <div key={pricing.size} className="grid grid-cols-3 gap-3">
                        <div className="col-span-1">
                          <label className="text-sm text-gray-600 capitalize">
                            {pricing.size.replace('_', ' ')}
                          </label>
                        </div>
                        <div>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                            <input
                              type="number"
                              step="0.01"
                              required
                              placeholder="Price"
                              className="input pl-10"
                              value={pricing.price}
                              onChange={(e) => {
                                const newPricing = [...serviceForm.pricing];
                                newPricing[index].price = e.target.value;
                                setServiceForm({ ...serviceForm, pricing: newPricing });
                              }}
                            />
                          </div>
                        </div>
                        <div>
                          <div className="relative">
                            <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                            <input
                              type="number"
                              required
                              placeholder="Minutes"
                              className="input pl-10"
                              value={pricing.durationMinutes}
                              onChange={(e) => {
                                const newPricing = [...serviceForm.pricing];
                                newPricing[index].durationMinutes = e.target.value;
                                setServiceForm({ ...serviceForm, pricing: newPricing });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <label className="label">Standard Pricing *</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">Price</label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <input
                          type="number"
                          step="0.01"
                          required
                          placeholder="Price"
                          className="input pl-10"
                          value={serviceForm.pricing[0]?.price || ''}
                          onChange={(e) => {
                            const newPricing = [{ size: 'standard', price: e.target.value, durationMinutes: serviceForm.pricing[0]?.durationMinutes || '' }];
                            setServiceForm({ ...serviceForm, pricing: newPricing });
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">Duration (minutes)</label>
                      <div className="relative">
                        <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <input
                          type="number"
                          required
                          placeholder="Minutes"
                          className="input pl-10"
                          value={serviceForm.pricing[0]?.durationMinutes || ''}
                          onChange={(e) => {
                            const newPricing = [{ size: 'standard', price: serviceForm.pricing[0]?.price || '', durationMinutes: e.target.value }];
                            setServiceForm({ ...serviceForm, pricing: newPricing });
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    💡 Size-based pricing is disabled. This is the standard price for all bookings.
                  </p>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowServiceModal(false)}
                  className="flex-1 btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="flex-1 btn btn-primary">
                  {editingService ? 'Update Service' : 'Create Service'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add-on Modal */}
      {showAddonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold mb-6">
              {editingAddon ? 'Edit Add-on' : 'Add New Add-on'}
            </h2>

            <form onSubmit={handleAddonSubmit} className="space-y-4">
              <div>
                <label className="label">Add-on Name *</label>
                <input
                  type="text"
                  required
                  className="input"
                  value={addonForm.name}
                  onChange={(e) => setAddonForm({ ...addonForm, name: e.target.value })}
                />
              </div>

              <div>
                <label className="label">Description</label>
                <textarea
                  className="input"
                  rows="3"
                  value={addonForm.description}
                  onChange={(e) => setAddonForm({ ...addonForm, description: e.target.value })}
                />
              </div>

              <div>
                <label className="label">Price *</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="number"
                    step="0.01"
                    required
                    className="input pl-10"
                    value={addonForm.price}
                    onChange={(e) => setAddonForm({ ...addonForm, price: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="label">Display Order</label>
                <input
                  type="number"
                  className="input"
                  value={addonForm.displayOrder}
                  onChange={(e) => setAddonForm({ ...addonForm, displayOrder: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddonModal(false)}
                  className="flex-1 btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="flex-1 btn btn-primary">
                  {editingAddon ? 'Update Add-on' : 'Create Add-on'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesEditor;
